import React, { FC, useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import store from '../store/configureStore';

import * as HeaderActions from '../actions/Header';
import * as MoffAPIActions from '../actions/MoffAPI';
import * as Header from '../constants/Header';
import * as UserListField from '../constants/UserListField';
import ScientificNursingCarePromotionList from '../components/Organisms/ScientificNursingCarePromotionList';
import {
  ScientificNursingCarePromotionAPI,
  createScientificNursingCarePromotionAPIInstance,
} from '../api/ScientificNursingCarePromotionAPI';
import _ from 'lodash';
import PageLoader from '../components/Atoms/PageLoader';

type AllState = ReturnType<typeof store.getState>;

const ScientificNursingCarePromotionListContainer: FC<RouteComponentProps<{ userId: string }>> = ({ match }) => {
  const userId = match.params.userId;
  const dispatch = useDispatch();
  const history = useHistory();
  const moffAPIGlobalState = useSelector((state: AllState) => state.moffAPI);

  const [user, setUser] = useState<UserListField.User>({} as UserListField.User);
  const [evaluations, setEvaluations] = useState<any[]>([] as any[]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [api, setApi] = useState<ScientificNursingCarePromotionAPI | null>(null);

  // APIインスタンスの初期化
  useEffect(() => {
    const initApi = async () => {
      try {
        const apiInstance = createScientificNursingCarePromotionAPIInstance();
        if (apiInstance) {
          await apiInstance.setAxiosInstanceWithJwtToken();
          setApi(apiInstance);
        }
      } catch (err) {
        console.error('API初期化エラー:', err);
        setError('認証エラーが発生しました。再度ログインしてください。');
        setLoading(false);
      }
    };
    initApi();
  }, []);

  // 評価データを取得する関数
  const fetchEvaluations = async (userId: string) => {
    if (!api) return;

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      // 直接APIメソッドを使用
      const response = await api.getEvaluationsFunc(userId);
      if (response.data && response.data.data) {
        setEvaluations(response.data.data);
      } else {
        setEvaluations([]);
      }
      setError(null);
    } catch (err) {
      console.error('評価データの取得に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setError('認証エラーが発生しました。再度ログインしてください。');
      } else {
        setError('評価データの取得に失敗しました。');
      }
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  // 評価データを削除する関数
  const handleDelete = async (userId: string, evaluationId: string) => {
    if (!api) return;

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      await api.deleteEvaluationFunc(userId, evaluationId);
      // 削除成功後、評価データを再取得
      await fetchEvaluations(userId);
      alert('評価データを削除しました。');
    } catch (err) {
      console.error('評価データの削除に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        alert('認証エラーが発生しました。再度ログインしてください。');
      } else {
        alert('評価データの削除に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  // 編集画面に遷移する関数
  const handleEdit = (userId: string, evaluationId: string) => {
    history.push(`/scientific_nursing_care_promotion_edit/${userId}/${evaluationId}`);
  };

  // 新規作成画面に遷移する関数
  const handleCreateNew = (userId: string) => {
    history.push(`/scientific_nursing_care_promotion_form/${userId}`);
  };

  useEffect(() => {
    (async () => {
      document.title = '科学的介護推進に関する評価';
      dispatch(HeaderActions.setPage(Header.SCIENTIFIC_NURSING_CARE_PROMOTION_LIST));
      // ユーザー情報の取得
      await dispatch(MoffAPIActions.getUsers());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!_.isEmpty(moffAPIGlobalState.result) && api) {
      const foundUser = moffAPIGlobalState.result.find((u: UserListField.User) => u.user_id === userId);
      if (foundUser) {
        setUser(foundUser);
        // ユーザー情報が取得できたら、評価データを取得
        fetchEvaluations(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moffAPIGlobalState.Loaded, moffAPIGlobalState.result, api]);

  if (error) {
    return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  }

  return (
    <>
      {loading || !moffAPIGlobalState.Loaded ? <PageLoader /> : null}
      {!loading && moffAPIGlobalState.Loaded ? (
        <ScientificNursingCarePromotionList
          userId={userId}
          user={user}
          evaluations={evaluations}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onCreateNew={handleCreateNew}
        />
      ) : null}
    </>
  );
};

export default ScientificNursingCarePromotionListContainer;
